// ============================================================
// TOKI PONA AUDIO
// Web Audio API sprite player with per-character voice profiles.
// Each SpeechBubble gets its own AudioChannel (effects chain)
// so multiple players can overlap.
//
// iOS Safari fallback: Web Audio API destination doesn't route to
// speakers on some iOS versions. We detect this and fall back to
// <audio> elements with playbackRate for pitch/speed.
// ============================================================

type SpeedKey = '0.5' | '1.0' | '1.5';
type WordEntry = Record<SpeedKey, { start: number; duration: number }>;
type Manifest = Record<string, WordEntry>;

// ── iOS detection ───────────────────────────────────────────

const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// ── Audio profile ────────────────────────────────────────────

export interface AudioProfile {
    name: string;
    word: string;           // toki pona archetype word
    playbackRate: number;   // speed+pitch multiplier (1.0 = normal)
    detune: number;         // additional pitch shift in cents
    filterType: BiquadFilterType;
    filterFreq: number;     // Hz
    filterQ: number;
    delayTime: number;      // echo delay in seconds (0 = no echo)
    delayFeedback: number;  // echo feedback 0–1
    gain: number;           // output volume multiplier
}

// ── 12 voice presets ─────────────────────────────────────────

export const VOICE_PROFILES: Record<string, AudioProfile> = {
    olin: {
        name: 'Lover', word: 'olin',
        playbackRate: 0.93, detune: -50,
        filterType: 'lowpass', filterFreq: 2500, filterQ: 1,
        delayTime: 0.12, delayFeedback: 0.2,
        gain: 0.9,
    },
    utala: {
        name: 'Warrior', word: 'utala',
        playbackRate: 0.82, detune: -100,
        filterType: 'lowpass', filterFreq: 3500, filterQ: 0.7,
        delayTime: 0, delayFeedback: 0,
        gain: 1.2,
    },
    ante: {
        name: 'Rival', word: 'ante',
        playbackRate: 1.08, detune: 80,
        filterType: 'highpass', filterFreq: 600, filterQ: 0.8,
        delayTime: 0, delayFeedback: 0,
        gain: 1.0,
    },
    ike: {
        name: 'Devil', word: 'ike',
        playbackRate: 0.72, detune: -200,
        filterType: 'lowpass', filterFreq: 1800, filterQ: 1.5,
        delayTime: 0.25, delayFeedback: 0.45,
        gain: 0.95,
    },
    pona: {
        name: 'Friend', word: 'pona',
        playbackRate: 1.0, detune: 0,
        filterType: 'lowpass', filterFreq: 6000, filterQ: 0.5,
        delayTime: 0, delayFeedback: 0,
        gain: 1.0,
    },
    walo: {
        name: 'Innocent', word: 'walo',
        playbackRate: 1.22, detune: 150,
        filterType: 'lowpass', filterFreq: 8000, filterQ: 0.5,
        delayTime: 0, delayFeedback: 0,
        gain: 0.85,
    },
    sewi: {
        name: 'Mystic', word: 'sewi',
        playbackRate: 1.05, detune: 100,
        filterType: 'bandpass', filterFreq: 1500, filterQ: 2,
        delayTime: 0.2, delayFeedback: 0.4,
        gain: 0.8,
    },
    suli: {
        name: 'Giant', word: 'suli',
        playbackRate: 0.65, detune: -300,
        filterType: 'lowpass', filterFreq: 1200, filterQ: 0.7,
        delayTime: 0.08, delayFeedback: 0.15,
        gain: 1.3,
    },
    kon: {
        name: 'Ghost', word: 'kon',
        playbackRate: 1.12, detune: 200,
        filterType: 'highpass', filterFreq: 1200, filterQ: 1,
        delayTime: 0.18, delayFeedback: 0.5,
        gain: 0.45,
    },
    mu: {
        name: 'Whisper', word: 'mu',
        playbackRate: 1.05, detune: 50,
        filterType: 'highpass', filterFreq: 2500, filterQ: 0.5,
        delayTime: 0, delayFeedback: 0,
        gain: 0.3,
    },
    lili: {
        name: 'Child', word: 'lili',
        playbackRate: 1.35, detune: 250,
        filterType: 'highpass', filterFreq: 400, filterQ: 0.5,
        delayTime: 0, delayFeedback: 0,
        gain: 0.9,
    },
    soweli: {
        name: 'Beast', word: 'soweli',
        playbackRate: 0.6, detune: -350,
        filterType: 'bandpass', filterFreq: 900, filterQ: 2.5,
        delayTime: 0.04, delayFeedback: 0.1,
        gain: 1.15,
    },
};

// ── AudioChannel — one per SpeechBubble ──────────────────────
// On non-iOS: full Web Audio API effects chain
// On iOS: <audio> element with playbackRate (no filter/echo).
//         Each channel owns a pre-loaded <audio> element to avoid
//         iOS rejecting play() on freshly-created elements.

export class AudioChannel {
    private manifest: Manifest;
    private profile: AudioProfile;

    // Web Audio mode (non-iOS)
    private ctx: AudioContext | null;
    private buffer: AudioBuffer | null;
    private filterNode: BiquadFilterNode | null = null;
    private delayNode: DelayNode | null = null;
    private feedbackGain: GainNode | null = null;
    private outputGain: GainNode | null = null;
    private currentSource: AudioBufferSourceNode | null = null;

    // HTML Audio mode (iOS) — persistent element, reused per word
    private useHTMLAudio: boolean;
    private audioEl: HTMLAudioElement | null = null;

    // Shared
    private timer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        ctx: AudioContext | null,
        buffer: AudioBuffer | null,
        manifest: Manifest,
        profile: AudioProfile,
        useHTMLAudio: boolean,
        audioEl: HTMLAudioElement | null,
    ) {
        this.ctx = ctx;
        this.buffer = buffer;
        this.manifest = manifest;
        this.profile = profile;
        this.useHTMLAudio = useHTMLAudio;
        this.audioEl = audioEl;

        // Build Web Audio effects chain (non-iOS only)
        if (!useHTMLAudio && ctx) {
            this.filterNode = ctx.createBiquadFilter();
            this.filterNode.type = profile.filterType;
            this.filterNode.frequency.value = profile.filterFreq;
            this.filterNode.Q.value = profile.filterQ;

            this.outputGain = ctx.createGain();
            this.outputGain.gain.value = profile.gain;

            this.filterNode.connect(this.outputGain);
            this.outputGain.connect(ctx.destination);

            if (profile.delayTime > 0 && profile.delayFeedback > 0) {
                this.delayNode = ctx.createDelay(2.0);
                this.delayNode.delayTime.value = profile.delayTime;

                this.feedbackGain = ctx.createGain();
                this.feedbackGain.gain.value = profile.delayFeedback;

                this.filterNode.connect(this.delayNode);
                this.delayNode.connect(this.outputGain);
                this.delayNode.connect(this.feedbackGain);
                this.feedbackGain.connect(this.delayNode);
            }
        }
    }

    /** Effective playback rate accounting for detune. */
    private get effectiveRate(): number {
        return this.profile.playbackRate * Math.pow(2, this.profile.detune / 1200);
    }

    play(word: string, speed: SpeedKey = '1.0'): Promise<void> {
        return new Promise((resolve, reject) => {
            const entry = this.manifest[word]?.[speed];
            if (!entry) return reject(new Error(`Unknown word/speed: "${word}" @ ${speed}`));

            // Cancel pending timer to prevent double-resolve, but for Web Audio
            // defer source disconnect until the new source is connected — avoids
            // leaving the filter chain with zero inputs during a render quantum.
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }

            if (this.useHTMLAudio) {
                this.stopSource();
                this.playHTML(entry, resolve);
            } else {
                this.playWebAudio(entry, resolve, word);
            }
        });
    }

    private playWebAudio(entry: { start: number; duration: number }, resolve: () => void, word?: string): void {
        if (!this.ctx || !this.buffer || !this.filterNode) {
            console.warn(`[TokiAudio] playWebAudio bail: ctx=${!!this.ctx} buffer=${!!this.buffer} filter=${!!this.filterNode} word="${word}"`);
            this.stopSource();
            resolve();
            return;
        }

        // source.start() silently produces nothing on a suspended context;
        // the setTimeout still fires, so visual timing looks correct but audio is absent.
        if (this.ctx.state === 'suspended') {
            console.warn(`[TokiAudio] ctx suspended at play time, resuming. word="${word}"`);
            this.ctx.resume();
        }

        try {
            const source = this.ctx.createBufferSource();
            source.buffer = this.buffer;
            source.playbackRate.value = this.profile.playbackRate;
            if (source.detune) {
                source.detune.value = this.profile.detune;
            }
            source.connect(this.filterNode);

            // Schedule at explicit currentTime — start(0) is spec-equivalent but
            // some browsers exhibit quirks with rapid sequential start(0) calls
            // where the literal 0 is clamped/rounded differently across render quanta.
            source.start(this.ctx.currentTime, entry.start, entry.duration);

            // NOW disconnect the old source — the new one is already connected and
            // started, so the filter chain was never left with zero inputs.
            const oldSource = this.currentSource;
            if (oldSource) {
                try { oldSource.stop(); } catch { /* already stopped */ }
                oldSource.disconnect();
            }

            this.currentSource = source;

            const ms = (entry.duration / this.effectiveRate) * 1000;
            this.timer = setTimeout(() => {
                this.timer = null;
                resolve();
            }, ms);
        } catch (err) {
            console.warn('[TokiAudio] playWebAudio failed:', err);
            resolve();
        }
    }

    private playHTML(entry: { start: number; duration: number }, resolve: () => void): void {
        if (!this.audioEl) { resolve(); return; }

        this.audioEl.currentTime = entry.start;
        this.audioEl.playbackRate = this.effectiveRate;
        this.audioEl.volume = Math.min(1, this.profile.gain);
        this.audioEl.play().catch(() => {});

        const ms = (entry.duration / this.effectiveRate) * 1000;
        this.timer = setTimeout(() => {
            this.timer = null;
            this.audioEl?.pause();
            resolve();
        }, ms);
    }

    /** Milliseconds for echo tail to decay after the last word. */
    getTailTime(): number {
        if (this.useHTMLAudio) return 0; // no echo on iOS
        const p = this.profile;
        if (p.delayTime <= 0 || p.delayFeedback <= 0) return 0;
        const echoes = Math.ceil(Math.log(0.05) / Math.log(p.delayFeedback));
        return p.delayTime * echoes * 1000;
    }

    private onDispose: ((el: HTMLAudioElement) => void) | null = null;

    /** Set a callback to return the <audio> element to the pool on dispose. */
    setDisposeCallback(cb: (el: HTMLAudioElement) => void): void {
        this.onDispose = cb;
    }

    stop(): void {
        this.stopSource();
        if (this.feedbackGain) this.feedbackGain.gain.value = 0;
        // Return the audio element to the pool
        if (this.audioEl && this.onDispose) {
            this.onDispose(this.audioEl);
            this.audioEl = null;
            this.onDispose = null;
        }
    }

    private stopSource(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (this.currentSource) {
            try { this.currentSource.stop(); } catch { /* already stopped */ }
            this.currentSource.disconnect();
            this.currentSource = null;
        }
        // Pause but keep the persistent <audio> element alive for reuse
        if (this.audioEl) {
            this.audioEl.pause();
        }
    }
}

// ── AudioContext constructor (Safari compat) ─────────────────

const AudioCtx: typeof AudioContext =
    (window as any).AudioContext || (window as any).webkitAudioContext;

// ── TokiPonaAudio — shared loader + channel factory ─────────

export class TokiPonaAudio {
    private ctx: AudioContext | null = null;
    private buffer: AudioBuffer | null = null;
    private manifest: Manifest | null = null;
    private rawAudioData: ArrayBuffer | null = null;
    private _loaded = false;
    private _fetching = false;
    private _fetched = false;
    private unlockBound: (() => void) | null = null;
    private _decoding = false;
    private _audioUnlocked = false;

    // iOS uses <audio> elements instead of Web Audio API for output
    private _useHTMLAudio = IS_IOS;

    // Audio sprite URL for <audio> element fallback
    private audioSrc = 'toki_pona_sprite.mp3';

    // Pool of pre-warmed <audio> elements created from user gesture (iOS)
    private audioPool: HTMLAudioElement[] = [];
    private static readonly POOL_SIZE = 8;

    setupUserGestureUnlock(): void {
        if (this.unlockBound) return;
        this.unlockBound = this.unlockAudio.bind(this);
        document.addEventListener('touchstart', this.unlockBound, true);
        document.addEventListener('touchend', this.unlockBound, true);
        document.addEventListener('click', this.unlockBound, true);
        document.addEventListener('keydown', this.unlockBound, true);
    }

    private unlockAudio(): void {
        if (this._audioUnlocked) {
            this.removeUnlockListeners();
            return;
        }

        if (this._useHTMLAudio) {
            if (!this.manifest) return; // not fetched yet, wait for next gesture

            // Create a pool of <audio> elements from this user gesture.
            // Each one is "primed" by calling play() then immediately pausing,
            // so iOS authorizes them for future programmatic playback.
            if (this.audioPool.length === 0) {
                for (let i = 0; i < TokiPonaAudio.POOL_SIZE; i++) {
                    const el = new Audio(this.audioSrc);
                    el.preload = 'auto';
                    // Prime: play then immediately pause to authorize the element
                    el.volume = 0.001;
                    el.play().then(() => el.pause()).catch(() => {});
                    el.volume = 1.0;
                    this.audioPool.push(el);
                }
            }

            this._audioUnlocked = true;
            this._loaded = true;
            this.removeUnlockListeners();
            return;
        }

        // Non-iOS: Web Audio API path
        if (!this.ctx) {
            try {
                this.ctx = new AudioCtx();
            } catch {
                return;
            }
        }

        const ctx = this.ctx;

        if (ctx.state === 'suspended') {
            const p = ctx.resume();
            if (p && typeof p.then === 'function') {
                p.then(() => this.tryDecode()).catch(() => this.tryDecode());
            }
            return;
        }

        this.tryDecode();

        if (this.buffer) {
            this._audioUnlocked = true;
            this._loaded = true;
            this.removeUnlockListeners();
        }
    }

    /** Get a pre-warmed <audio> element from the pool (iOS only). */
    private takeAudioElement(): HTMLAudioElement | null {
        // Reuse from pool if available
        if (this.audioPool.length > 0) {
            return this.audioPool.pop()!;
        }
        // Fallback: create new (may not work outside gesture on iOS)
        const el = new Audio(this.audioSrc);
        el.preload = 'auto';
        return el;
    }

    /** Return an <audio> element to the pool for reuse. */
    returnAudioElement(el: HTMLAudioElement): void {
        el.pause();
        if (this.audioPool.length < TokiPonaAudio.POOL_SIZE) {
            this.audioPool.push(el);
        }
    }

    private tryDecode(): void {
        if (!this.ctx || !this.rawAudioData || this.buffer || this._decoding) return;

        this._decoding = true;
        const dataCopy = this.rawAudioData.slice(0);

        this.ctx.decodeAudioData(
            dataCopy,
            (decoded) => {
                this.buffer = decoded;
                this._loaded = true;
                this._decoding = false;
                this.rawAudioData = null;
            },
            () => {
                this._decoding = false;
            },
        );
    }

    private removeUnlockListeners(): void {
        if (!this.unlockBound) return;
        document.removeEventListener('touchstart', this.unlockBound, true);
        document.removeEventListener('touchend', this.unlockBound, true);
        document.removeEventListener('click', this.unlockBound, true);
        document.removeEventListener('keydown', this.unlockBound, true);
        this.unlockBound = null;
    }

    async load(): Promise<void> {
        if (this._fetching || this._fetched) return;
        this._fetching = true;

        try {
            const [manifestResp, audioResp] = await Promise.all([
                fetch('audio_manifest.json'),
                fetch(this.audioSrc),
            ]);
            if (!manifestResp.ok || !audioResp.ok) return;

            this.manifest = (await manifestResp.json()) as Manifest;

            if (this._useHTMLAudio) {
                // iOS: don't need AudioContext or decoded buffer
                // Just consume the response so the browser caches the MP3
                await audioResp.arrayBuffer();
                this._fetched = true;
                // _loaded set to true in unlockAudio once gesture primes the session
                return;
            }

            // Non-iOS: store raw data for decoding
            this.rawAudioData = await audioResp.arrayBuffer();
            this._fetched = true;
            this.tryDecode();
        } catch {
            // fetch failed
        } finally {
            this._fetching = false;
        }
    }

    get loaded(): boolean {
        return this._loaded;
    }

    createChannel(profile: AudioProfile = VOICE_PROFILES.pona): AudioChannel | null {
        if (!this.manifest) {
            console.warn('[TokiAudio] createChannel failed: manifest not loaded');
            return null;
        }

        if (this._useHTMLAudio) {
            // iOS: hand out a pre-warmed <audio> element from the pool
            const el = this.takeAudioElement();
            const channel = new AudioChannel(null, null, this.manifest, profile, true, el);
            channel.setDisposeCallback((returnedEl) => this.returnAudioElement(returnedEl));
            return channel;
        }

        // Non-iOS: need full Web Audio API
        if (!this.ctx || !this.buffer) {
            console.warn(`[TokiAudio] createChannel failed: ctx=${!!this.ctx} buffer=${!!this.buffer} fetched=${this._fetched} decoding=${this._decoding}`);
            // Try to bootstrap audio context if we have raw data but no context yet
            if (!this.ctx && this.rawAudioData) {
                try {
                    this.ctx = new AudioCtx();
                    this.ctx.resume().then(() => this.tryDecode()).catch(() => this.tryDecode());
                } catch { /* no AudioContext support */ }
            } else if (this.ctx && !this.buffer) {
                // Context exists but buffer not decoded — kick decode
                this.tryDecode();
            }
            return null;
        }
        if (this.ctx.state === 'suspended') this.ctx.resume();
        return new AudioChannel(this.ctx, this.buffer, this.manifest, profile, false, null);
    }
}
